import { ReactNode, useEffect } from 'react';
import { useAuth } from './PrivyAuthProvider';
import { readCentralLoginUrl } from './config';
import { buildLoginUrl } from './redirect';

interface AuthGateProps {
  children: ReactNode;
  /** Shown while Privy SDK is bootstrapping. */
  loading?: ReactNode;
  /**
   * Rendered when unauthenticated AND no central login URL is configured.
   * In central-login mode (VITE_LOGIN_URL set), the user is redirected away
   * and this never renders.
   */
  fallback: ReactNode;
}

/**
 * Route guard.
 *
 * Two modes, chosen by presence of `VITE_LOGIN_URL` at build time:
 *  - **Central mode** (VITE_LOGIN_URL is set):
 *    Unauthenticated → redirect to central login with ?redirect=<current URL>.
 *    `fallback` is only rendered as a brief flash before redirect.
 *  - **Standalone mode** (VITE_LOGIN_URL empty/unset):
 *    Unauthenticated → render `fallback` inline (typically <LoginPage />).
 */
export function AuthGate({ children, loading, fallback }: AuthGateProps) {
  const { ready, authenticated } = useAuth();
  const centralLoginUrl = readCentralLoginUrl();

  useEffect(() => {
    if (!ready) return;
    if (authenticated) return;
    if (!centralLoginUrl) return;
    // Central mode: hard-redirect to login page, preserving intended destination
    window.location.href = buildLoginUrl(centralLoginUrl);
  }, [ready, authenticated, centralLoginUrl]);

  if (!ready) return <>{loading ?? <DefaultLoading />}</>;
  if (!authenticated) {
    // In central mode the useEffect above will redirect immediately;
    // render loading for the brief window before navigation.
    if (centralLoginUrl) return <>{loading ?? <DefaultLoading />}</>;
    return <>{fallback}</>;
  }
  return <>{children}</>;
}

function DefaultLoading() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: '#0d0d0d',
        color: '#888',
        fontFamily: 'system-ui',
        fontSize: 14,
      }}
    >
      Loading…
    </div>
  );
}
